import { Typography, Divider, Stack, Button, IconButton, Box, Chip } from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';

import {
  ApiResponse_Create_Group_Negotiation,
  useCreateGroupNegotiation,
} from '@/api/endpoints/community/useCreateGroupNegotiation';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { formatPhoneNumber } from '@/utils/phone';

import FormDatePicker from '@/components/form/FormDatePicker';
import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

interface CreateGroupNegotiationModalProps {
  communityId: number;
  onClose: () => void;
  onGroupNegotiationCreated?: (groupNegotiation: ApiResponse_Create_Group_Negotiation) => void;
}

interface PricingTier {
  minHomes: string;
  maxHomes: string;
  pricePerHome: string;
}

interface FormData {
  title: string;
  description: string;
  end_date: string;
  max_member_count: string;
  negotiated_cost: string;
  original_cost: string;
  vendor_name: string;
  vendor_email: string;
  vendor_phone: string;
  usePricingTiers: boolean;
  pricingTiers: PricingTier[];
}

const CreateGroupNegotiationModal: React.FC<CreateGroupNegotiationModalProps> = ({
  communityId,
  onClose,
  onGroupNegotiationCreated,
}) => {
  // Calculate default end date (30 days from now)
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const form = useForm<FormData>({
    defaultValues: {
      title: '',
      description: '',
      end_date: getDefaultEndDate(),
      max_member_count: '',
      negotiated_cost: '',
      original_cost: '',
      vendor_name: '',
      vendor_email: '',
      vendor_phone: '',
      usePricingTiers: false,
      pricingTiers: [
        { minHomes: '1', maxHomes: '4', pricePerHome: '' },
        { minHomes: '5', maxHomes: '9', pricePerHome: '' },
        { minHomes: '10', maxHomes: '19', pricePerHome: '' },
        { minHomes: '20', maxHomes: '', pricePerHome: '' },
      ],
    },
  });

  const {
    register,
    setValue,
    watch,
    control,
    formState: { errors },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'pricingTiers',
  });

  const usePricingTiers = watch('usePricingTiers');

  const createGroupNegotiationMutation = useCreateGroupNegotiation(communityId, {
    onSuccess: data => {
      onGroupNegotiationCreated?.(data);
      onClose();
    },
  });

  const handleVendorPhoneChange = (value: string) => {
    const formattedValue = formatPhoneNumber(value);
    setValue('vendor_phone', formattedValue);
  };

  const onSubmit = async (data: FormData) => {
    const metadata: Record<string, unknown> = {};

    if (data.usePricingTiers && data.pricingTiers.length > 0) {
      // Convert pricing tiers to the expected format
      const validTiers = data.pricingTiers
        .filter(tier => tier.pricePerHome && parseFloat(tier.pricePerHome) > 0)
        .map(tier => ({
          minHomes: parseInt(tier.minHomes, 10),
          maxHomes: tier.maxHomes ? parseInt(tier.maxHomes, 10) : undefined,
          pricePerHome: parseFloat(tier.pricePerHome),
        }));

      if (validTiers.length > 0) {
        metadata.pricingTiers = validTiers;
      }
    } else {
      // Use single pricing model
      if (data.original_cost) {
        metadata.originalCost = parseFloat(data.original_cost);
      }

      if (data.negotiated_cost && data.original_cost) {
        const originalCost = parseFloat(data.original_cost);
        const negotiatedCost = parseFloat(data.negotiated_cost);
        const savingsAmount = originalCost - negotiatedCost;
        metadata.savingsAmount = savingsAmount > 0 ? savingsAmount : 0;
      }
    }

    // Add vendor information to metadata if provided
    if (data.vendor_name || data.vendor_email || data.vendor_phone) {
      metadata.vendor = {
        name: data.vendor_name || '',
        email: data.vendor_email || '',
        phone: data.vendor_phone ? data.vendor_phone.replace(/\D/g, '') : '',
      };
    }

    await createGroupNegotiationMutation.mutateAsync({
      title: data.title,
      description: data.description || undefined,
      end_date: data.end_date,
      max_member_count: data.max_member_count ? parseInt(data.max_member_count, 10) : undefined,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    });
  };

  return (
    <ModalWithForm
      form={form}
      onClose={onClose}
      title="Create Save Together"
      onSubmit={onSubmit}
      buttonLabel="Add Save Together"
      maxWidth="lg"
    >
      <FormTextField
        label="Title"
        placeholder="Enter save together title"
        required
        fullWidth
        control={form.control}
        error={!!errors.title}
        helperText={errors.title?.message}
        {...register('title', {
          required: 'Title is required',
          maxLength: {
            value: 255,
            message: 'Title must be less than 255 characters',
          },
        })}
      />

      <FormTextField
        label="Description"
        placeholder="Describe what this save together group is about"
        multiline
        rows={4}
        fullWidth
        control={form.control}
        error={!!errors.description}
        helperText={errors.description?.message}
        {...register('description', {
          maxLength: {
            value: 2000,
            message: 'Description must be less than 2000 characters',
          },
        })}
      />

      <FormDatePicker
        name="end_date"
        label="End Date"
        control={form.control}
        error={!!errors.end_date}
        helperText={errors.end_date?.message || 'Default is 30 days from today'}
      />

      <FormTextField
        label="Max Members (Optional)"
        placeholder="Enter maximum number of members"
        textFieldProps={{ type: 'number' }}
        fullWidth
        control={form.control}
        error={!!errors.max_member_count}
        helperText={errors.max_member_count?.message || 'Leave empty for unlimited members'}
        {...register('max_member_count', {
          min: {
            value: 2,
            message: 'Minimum 2 members required',
          },
          max: {
            value: 1000,
            message: 'Maximum 1000 members allowed',
          },
        })}
      />

      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Pricing Options
        </Typography>
      </Divider>

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label="Single Price"
            onClick={() => setValue('usePricingTiers', false)}
            color={!usePricingTiers ? 'primary' : 'default'}
            variant={!usePricingTiers ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Tiered Pricing"
            onClick={() => setValue('usePricingTiers', true)}
            color={usePricingTiers ? 'primary' : 'default'}
            variant={usePricingTiers ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {usePricingTiers
            ? 'Set different prices based on group size'
            : 'Set a single price for all members'}
        </Typography>
      </Box>

      {usePricingTiers ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Pricing Tiers
          </Typography>
          <Stack spacing={2}>
            {fields.map((field, index) => (
              <Box
                key={field.id}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <FormTextField
                    label="Min Homes"
                    placeholder="1"
                    textFieldProps={{
                      type: 'number',
                      inputProps: { min: '1' },
                      size: 'small',
                    }}
                    control={form.control}
                    {...register(`pricingTiers.${index}.minHomes`, {
                      required: 'Min homes is required',
                      min: {
                        value: 1,
                        message: 'Minimum 1 home',
                      },
                    })}
                    error={!!errors.pricingTiers?.[index]?.minHomes}
                    helperText={errors.pricingTiers?.[index]?.minHomes?.message}
                    sx={{ flex: 1 }}
                  />
                  <FormTextField
                    label="Max Homes"
                    placeholder={index === fields.length - 1 ? 'Unlimited' : '10'}
                    textFieldProps={{
                      type: 'number',
                      inputProps: { min: '1' },
                      size: 'small',
                    }}
                    control={form.control}
                    {...register(`pricingTiers.${index}.maxHomes`, {
                      validate: value => {
                        if (!value && index === fields.length - 1) return true; // Last tier can be unlimited
                        const minHomes = form.getValues(`pricingTiers.${index}.minHomes`);
                        if (value && parseInt(value, 10) < parseInt(minHomes, 10)) {
                          return 'Max must be greater than min';
                        }
                        return true;
                      },
                    })}
                    error={!!errors.pricingTiers?.[index]?.maxHomes}
                    helperText={errors.pricingTiers?.[index]?.maxHomes?.message}
                    sx={{ flex: 1 }}
                  />
                  <FormTextField
                    label="Price/Home"
                    placeholder="0.00"
                    textFieldProps={{
                      type: 'number',
                      inputProps: { step: '0.01', min: '0' },
                      size: 'small',
                      InputProps: {
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      },
                    }}
                    control={form.control}
                    {...register(`pricingTiers.${index}.pricePerHome`, {
                      required: 'Price is required',
                      pattern: {
                        value: /^\d+(\.\d{0,2})?$/,
                        message: 'Valid price required',
                      },
                    })}
                    error={!!errors.pricingTiers?.[index]?.pricePerHome}
                    helperText={errors.pricingTiers?.[index]?.pricePerHome?.message}
                    sx={{ flex: 1.5 }}
                  />
                  <IconButton
                    onClick={() => remove(index)}
                    disabled={fields.length <= 1}
                    size="small"
                    sx={{ mt: 0.5 }}
                  >
                    <Trash01Icon />
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Button
            startIcon={<PlusIcon />}
            onClick={() => append({ minHomes: '', maxHomes: '', pricePerHome: '' })}
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
          >
            Add Pricing Tier
          </Button>
        </Box>
      ) : (
        <>
          <FormTextField
            label="Original Cost (Optional)"
            placeholder="0.00"
            textFieldProps={{
              type: 'number',
              inputProps: { step: '0.01', min: '0' },
              InputProps: {
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              },
            }}
            fullWidth
            control={form.control}
            error={!!errors.original_cost}
            helperText={errors.original_cost?.message || 'Current cost per member before savings'}
            {...register('original_cost', {
              min: {
                value: 0,
                message: 'Original cost must be positive',
              },
              max: {
                value: 10000,
                message: 'Original cost must be less than $10,000',
              },
              pattern: {
                value: /^\d+(\.\d{0,2})?$/,
                message: 'Please enter a valid dollar amount',
              },
            })}
          />

          <FormTextField
            label="Negotiated Cost (Optional)"
            placeholder="0.00"
            textFieldProps={{
              type: 'number',
              inputProps: { step: '0.01', min: '0' },
              InputProps: {
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              },
            }}
            fullWidth
            control={form.control}
            error={!!errors.negotiated_cost}
            helperText={
              errors.negotiated_cost?.message || 'Negotiated cost per member after group savings'
            }
            {...register('negotiated_cost', {
              min: {
                value: 0,
                message: 'Negotiated cost must be positive',
              },
              max: {
                value: 10000,
                message: 'Negotiated cost must be less than $10,000',
              },
              pattern: {
                value: /^\d+(\.\d{0,2})?$/,
                message: 'Please enter a valid dollar amount',
              },
              validate: value => {
                const originalCost = form.getValues('original_cost');
                if (originalCost && value) {
                  const originalCostNum = parseFloat(originalCost);
                  const negotiatedCostNum = parseFloat(value);
                  if (negotiatedCostNum > originalCostNum) {
                    return 'Negotiated cost cannot be higher than original cost';
                  }
                }
                return true;
              },
            })}
          />
        </>
      )}

      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Provider Contact Information (Optional)
        </Typography>
      </Divider>

      <FormTextField
        label="Provider Name"
        placeholder="Enter provider name"
        fullWidth
        control={form.control}
        error={!!errors.vendor_name}
        helperText={errors.vendor_name?.message}
        {...register('vendor_name', {
          maxLength: {
            value: 255,
            message: 'Provider name must be less than 255 characters',
          },
        })}
      />

      <FormTextField
        label="Provider Email"
        placeholder="provider@example.com"
        fullWidth
        control={form.control}
        error={!!errors.vendor_email}
        helperText={errors.vendor_email?.message}
        {...register('vendor_email', {
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Please enter a valid email address',
          },
          maxLength: {
            value: 255,
            message: 'Email must be less than 255 characters',
          },
        })}
      />

      <FormTextField
        label="Provider Phone"
        placeholder="(555) 123-4567"
        fullWidth
        control={form.control}
        error={!!errors.vendor_phone}
        helperText={errors.vendor_phone?.message}
        {...register('vendor_phone', {
          pattern: {
            value: /^\(\d{3}\)\s\d{3}-\d{4}$/,
            message: 'Phone number must be in format (999) 999-9999',
          },
          onChange: e => handleVendorPhoneChange(e.target.value),
        })}
      />
    </ModalWithForm>
  );
};

export default CreateGroupNegotiationModal;
